/**
 * StripeSuccessPage — Pagina post-pagamento Stripe riuscito.
 * Route: /ordine-confermato?session_id=cs_xxx (pubblica)
 *
 * Stripe redireziona qui dopo un pagamento completato.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, ShieldCheck, Mail, ArrowLeft,
  CreditCard, UserPlus, Rocket, Sparkles, PartyPopper,
} from 'lucide-react';
import { API_ENDPOINTS } from '../../Config/api';

const TIMELINE_STEPS = [
  { icon: CreditCard, label: 'Pagamento confermato', desc: 'Il pagamento è stato ricevuto correttamente.', done: true },
  { icon: UserPlus,   label: 'Creazione account',    desc: 'Stiamo preparando il tuo account A-Sight.',   done: false },
  { icon: Mail,       label: 'Email di attivazione',  desc: 'Riceverai le credenziali per accedere.',       done: false },
  { icon: Rocket,     label: 'Accesso al portale',    desc: 'Potrai iniziare a utilizzare i tuoi servizi.', done: false },
];

/* Confetti burst — lightweight CSS-only confetti */
function ConfettiBurst() {
  const pieces = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    color: ['#FFD700', '#10B981', '#3B82F6', '#F59E0B', '#EC4899', '#8B5CF6'][i % 6],
    left: `${10 + Math.random() * 80}%`,
    delay: Math.random() * 0.5,
    rotation: Math.random() * 360,
    size: 6 + Math.random() * 6,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -20, x: 0, opacity: 1, rotate: 0 }}
          animate={{
            y: [0, 300 + Math.random() * 200],
            x: [0, (Math.random() - 0.5) * 200],
            opacity: [1, 1, 0],
            rotate: p.rotation + 720,
          }}
          transition={{ duration: 2 + Math.random(), delay: p.delay, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            left: p.left,
            top: 0,
            width: p.size,
            height: p.size,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            background: p.color,
          }}
        />
      ))}
    </div>
  );
}

export default function StripeSuccessPage() {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);
  const ref = localStorage.getItem('asight_channel') || '';

  const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const defaultLogoUrl = `${apiBaseUrl}/api/auth/media/branding/a-sight_LOGO_DEF.png`;
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(null);

  const withRef = (path) => {
    if (ref) {
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}ref=${encodeURIComponent(ref)}`;
    }
    return path;
  };

  useEffect(() => {
    const url = ref
        ? `${API_ENDPOINTS.brandingLogo}?ref=${encodeURIComponent(ref)}`
        : API_ENDPOINTS.brandingLogo;
    fetch(url)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
            setBrandingLogoUrl(data?.logo_url || defaultLogoUrl);
        })
        .catch(() => { setBrandingLogoUrl(defaultLogoUrl); });
  }, [ref]);

  useEffect(() => {
    const t = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-gray-50 flex flex-col relative">
      {/* Confetti */}
      <AnimatePresence>{showConfetti && <ConfettiBurst />}</AnimatePresence>

      {/* Header */}
      <header className="bg-gradient-to-r from-white via-yellow-100 to-yellow-300 border-b border-yellow-200 shadow-sm relative z-10">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center justify-center" style={{ minHeight: 80 }}>
          {brandingLogoUrl && <img src={brandingLogoUrl} alt="Logo" draggable="false" style={{ maxHeight: 80, userSelect: 'none' }} />}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-12 flex-1 w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-xl border border-green-100 p-8 md:p-10 relative overflow-hidden"
        >
          {/* Subtle green gradient accent */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-green-400 via-emerald-400 to-green-500" />

          {/* Animated check */}
          <motion.div
            className="flex justify-center mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-green-100 animate-ping opacity-20" style={{ animationDuration: '2.5s' }} />
              <div className="absolute -inset-3 rounded-full bg-green-50 animate-pulse opacity-40" style={{ animationDuration: '3s' }} />
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-200 relative">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <PartyPopper className="w-5 h-5 text-yellow-500" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Pagamento Completato!
              </h2>
              <PartyPopper className="w-5 h-5 text-yellow-500 -scale-x-100" />
            </div>
            <p className="text-gray-500 text-center mb-8 max-w-md mx-auto">
              Grazie per il tuo acquisto. Il servizio sarà attivato a breve.
            </p>
          </motion.div>

          {/* Timeline – Cosa succede ora */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-6 max-w-lg mx-auto mb-8 border border-gray-100"
          >
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-4 h-4 text-yellow-500" />
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                Cosa succede ora
              </h3>
            </div>
            <ol className="relative">
              {/* Vertical line */}
              <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-gray-200" />
              {TIMELINE_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + i * 0.15 }}
                    className={`flex items-start gap-4 relative ${i < TIMELINE_STEPS.length - 1 ? 'pb-5' : ''}`}
                  >
                    <div className={`
                      flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center relative z-10
                      transition-all duration-300
                      ${step.done
                        ? 'bg-gradient-to-br from-green-400 to-emerald-500 shadow-sm shadow-green-200'
                        : 'bg-white border-2 border-gray-200'}
                    `}>
                      {step.done
                        ? <CheckCircle2 className="w-4 h-4 text-white" />
                        : <Icon className="w-3.5 h-3.5 text-gray-400" />}
                    </div>
                    <div className="pt-0.5">
                      <p className={`text-sm font-semibold ${step.done ? 'text-green-700' : 'text-gray-700'}`}>
                        {step.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                    </div>
                    {step.done && (
                      <span className="ml-auto text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-100 self-center">
                        Completato
                      </span>
                    )}
                  </motion.li>
                );
              })}
            </ol>
          </motion.div>

          {/* Email callout */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="flex items-center gap-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-5 text-left max-w-lg mx-auto mb-8"
          >
            <div className="p-3 bg-yellow-100 rounded-xl flex-shrink-0">
              <Mail className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="text-sm text-gray-800">
              <p className="font-bold">Controlla la tua email</p>
              <p className="text-gray-600 mt-0.5">
                Riceverai un'email con il link per attivare il tuo account e accedere al portale A-Sight.
                Verifica anche la cartella spam.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-center"
          >
            <button
              onClick={() => navigate(withRef('/'))}
              className="inline-flex items-center gap-2 text-yellow-600 hover:text-yellow-800 text-sm font-medium transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Torna alla home
            </button>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-yellow-200 bg-gradient-to-r from-white via-yellow-50 to-yellow-100 relative z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} A-Sight — Innova-ware. Tutti i diritti riservati.
        </div>
      </footer>
    </div>
  );
}
